import React, { useEffect, useMemo, useState } from "react";
import {
  PillMenu,
  SkeletonCard,
  deriveCaseStage,
  type CaseStage,
} from "@auction-eth/ui-kit";
import type { Case, FastSnapshot } from "@auction-eth/shared-types";
import { caseMatchesSearchQuery } from "@auction-eth/shared-utils";
import { useCases } from "../hooks/useCases.js";

// ─── Constants ───────────────────────────────────────────────────────────────

const STAGE_FILTERS: Array<{ id: string; label: string }> = [
  { id: "all", label: "All Cases" },
  { id: "initiation", label: "Initiation" },
  { id: "auction_approval", label: "Auction Approval" },
  { id: "winner_approval", label: "Winner Approval" },
  { id: "settlement", label: "Settlement" },
  { id: "closed", label: "Closed" },
];

const STEP_LABELS: Record<string, string> = {
  step_1_initiation: "Case Initiation",
  step_2_field_dispatch: "Field Seller Dispatch",
  step_3_field_verification: "Field Verification",
  step_4_hub_dispatch: "Hub Dispatch",
  step_5_hub_witness: "Hub Witness",
  step_6_hub_processing: "Hub Processing",
  step_7_expert_dispatch: "Expert Dispatch",
  step_8_expert_valuation: "Expert Valuation",
  step_9_cataloging: "Cataloging",
  step_10_auction_approval: "Auction Approval",
  step_11_market_listing: "Market Listing",
  step_12_hub_inspection: "Hub Inspection",
  step_13_bidding_approval: "Bidding Approval",
  step_14_market_bidding: "Bidding Active",
  step_15_market_closed: "Auction Closed",
  step_16_winner_approval: "Winner Approval",
  step_17_winner_posted: "Winner Posted",
  step_18_settlement: "Settlement",
};

const STAGE_COLORS: Record<string, string> = {
  initiation: "#6366f1",
  auction_approval: "#f59e0b",
  winner_approval: "#3b82f6",
  settlement: "#10b981",
  closed: "#64748b",
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatDate(iso: string | undefined): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

function stageBadge(stage: string, color: string): React.ReactElement {
  return (
    <span style={{
      fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em",
      padding: "3px 9px", borderRadius: 999,
      background: `${color}18`, color, border: `1px solid ${color}40`, whiteSpace: "nowrap",
    }}>
      {stage.replace(/_/g, " ")}
    </span>
  );
}

// ─── Card view components ─────────────────────────────────────────────────────

function InfoPill({ icon, label, value }: { icon: string; label: string; value: string | null | undefined }): React.ReactElement {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
      <span style={{ fontSize: 12 }}>{icon}</span>
      <div>
        <span style={{ fontSize: 10, color: "var(--muted)", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em", display: "block" }}>{label}</span>
        <span style={{ fontSize: 12, color: "var(--ink)", fontWeight: 600 }}>{value ?? "—"}</span>
      </div>
    </div>
  );
}

function RichCaseCard({ c, stage, onClick }: { c: Case; stage: CaseStage; onClick: () => void }): React.ReactElement {
  const fast = (c as unknown as { fast_snapshot?: FastSnapshot }).fast_snapshot;
  const borrowerName = fast?.borrower_info?.full_name ?? c.borrower_id ?? "Unknown Borrower";
  const assetCategory = fast?.asset_info?.asset_category ?? c.asset_category ?? "—";
  const assetType = fast?.asset_info?.asset_type ?? "—";
  const loanAmount = fast?.loan_detail?.amount;
  const loanCurrency = fast?.loan_detail?.currency ?? "ETB";
  const stepLabel = STEP_LABELS[c.current_step_id] ?? c.current_step_id;
  const stageColor = STAGE_COLORS[stage] ?? "#6366f1";
  const createdAt = formatDate((c as unknown as { created_at?: string }).created_at);

  return (
    <div
      role="button" tabIndex={0} onClick={onClick}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") onClick(); }}
      aria-label={`View case ${c.case_id}`}
      style={{
        background: "var(--panel)", border: "1px solid var(--border)",
        borderLeft: `5px solid ${stageColor}`, borderRadius: "var(--radius)",
        padding: "18px 20px", cursor: "pointer", boxShadow: "var(--shadow)",
        transition: "transform 0.15s ease, box-shadow 0.15s ease",
        display: "flex", flexDirection: "column", gap: 10,
      }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLDivElement).style.transform = "translateY(-2px)";
        (e.currentTarget as HTMLDivElement).style.boxShadow = "0 6px 24px rgba(15,23,42,0.12)";
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLDivElement).style.transform = "";
        (e.currentTarget as HTMLDivElement).style.boxShadow = "var(--shadow)";
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div>
          <p style={{ margin: 0, fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--muted)" }}>{c.case_id}</p>
          <p style={{ margin: "4px 0 0", fontSize: 16, fontWeight: 800, color: "var(--ink)" }}>{borrowerName}</p>
        </div>
        {stageBadge(stage, stageColor)}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "6px 12px" }}>
        <InfoPill icon="📦" label="Asset" value={`${assetCategory} · ${assetType}`} />
        <InfoPill icon="🏦" label="Loan" value={c.linked_loan_id} />
        {loanAmount != null && <InfoPill icon="💰" label="Amount" value={`${loanCurrency} ${loanAmount.toLocaleString()}`} />}
        {c.asset_location && <InfoPill icon="📍" label="Zone" value={c.asset_location} />}
      </div>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingTop: 8, borderTop: "1px solid var(--border)" }}>
        <span style={{ fontSize: 11, color: "var(--muted)" }}>
          Step: <strong style={{ color: stageColor }}>{stepLabel}</strong>
        </span>
        <span style={{ fontSize: 11, color: "var(--muted)" }}>Created {createdAt}</span>
      </div>
    </div>
  );
}

// ─── Table view ───────────────────────────────────────────────────────────────

const TH_STYLE: React.CSSProperties = {
  padding: "10px 14px", fontSize: 11, fontWeight: 700, textTransform: "uppercase",
  letterSpacing: "0.05em", color: "var(--muted)", textAlign: "left",
  borderBottom: "2px solid var(--border)", background: "var(--bg)",
  whiteSpace: "nowrap",
};

const TD_STYLE: React.CSSProperties = {
  padding: "12px 14px", fontSize: 13, color: "var(--ink)",
  borderBottom: "1px solid var(--border)", verticalAlign: "middle",
};

function CaseTable({ cases, getCaseStage, onSelectCase }: {
  cases: Case[];
  getCaseStage: (c: Case) => CaseStage;
  onSelectCase: (id: string) => void;
}): React.ReactElement {
  return (
    <div style={{ background: "var(--panel)", border: "1px solid var(--border)", borderRadius: "var(--radius)", boxShadow: "var(--shadow)", overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "auto" }}>
        <thead>
          <tr>
            <th style={TH_STYLE}>Case ID</th>
            <th style={TH_STYLE}>Borrower</th>
            <th style={TH_STYLE}>Loan Ref</th>
            <th style={TH_STYLE}>Asset</th>
            <th style={TH_STYLE}>Amount</th>
            <th style={TH_STYLE}>Zone</th>
            <th style={TH_STYLE}>Stage</th>
            <th style={TH_STYLE}>Current Step</th>
            <th style={TH_STYLE}>Created</th>
            <th style={{ ...TH_STYLE, textAlign: "center" }}>Action</th>
          </tr>
        </thead>
        <tbody>
          {cases.map((c, idx) => {
            const fast = (c as unknown as { fast_snapshot?: FastSnapshot }).fast_snapshot;
            const stage = getCaseStage(c);
            const stageColor = STAGE_COLORS[stage] ?? "#6366f1";
            const borrowerName = fast?.borrower_info?.full_name ?? c.borrower_id ?? "—";
            const assetCategory = fast?.asset_info?.asset_category ?? c.asset_category ?? "—";
            const loanAmount = fast?.loan_detail?.amount;
            const loanCurrency = fast?.loan_detail?.currency ?? "ETB";
            const stepLabel = STEP_LABELS[c.current_step_id] ?? c.current_step_id.replace(/_/g, " ");
            const createdAt = formatDate((c as unknown as { created_at?: string }).created_at);
            const isEven = idx % 2 === 0;

            return (
              <tr
                key={c.case_id}
                style={{
                  background: isEven ? "var(--panel)" : "var(--bg)",
                  cursor: "pointer",
                  transition: "background 0.12s",
                }}
                onClick={() => onSelectCase(c.case_id)}
                onMouseEnter={(e) => (e.currentTarget as HTMLTableRowElement).style.background = `${stageColor}0d`}
                onMouseLeave={(e) => (e.currentTarget as HTMLTableRowElement).style.background = isEven ? "var(--panel)" : "var(--bg)"}
              >
                {/* Case ID */}
                <td style={TD_STYLE}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: "var(--muted)", fontFamily: "monospace" }}>{c.case_id}</span>
                </td>
                {/* Borrower */}
                <td style={{ ...TD_STYLE, fontWeight: 700, maxWidth: 180 }}>
                  <span style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {borrowerName}
                  </span>
                </td>
                {/* Loan Ref */}
                <td style={{ ...TD_STYLE, color: "var(--muted)", fontSize: 12 }}>{c.linked_loan_id}</td>
                {/* Asset */}
                <td style={TD_STYLE}>{assetCategory}</td>
                {/* Amount */}
                <td style={{ ...TD_STYLE, fontWeight: 600 }}>
                  {loanAmount != null ? `${loanCurrency} ${loanAmount.toLocaleString()}` : "—"}
                </td>
                {/* Zone */}
                <td style={{ ...TD_STYLE, color: "var(--muted)", fontSize: 12 }}>
                  {c.asset_location ?? "—"}
                </td>
                {/* Stage badge */}
                <td style={TD_STYLE}>{stageBadge(stage, stageColor)}</td>
                {/* Step */}
                <td style={{ ...TD_STYLE, fontSize: 12, color: stageColor, fontWeight: 600, maxWidth: 160 }}>
                  <span style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {stepLabel}
                  </span>
                </td>
                {/* Created */}
                <td style={{ ...TD_STYLE, color: "var(--muted)", fontSize: 12, whiteSpace: "nowrap" }}>{createdAt}</td>
                {/* Action */}
                <td style={{ ...TD_STYLE, textAlign: "center" }}>
                  <button
                    onClick={(e) => { e.stopPropagation(); onSelectCase(c.case_id); }}
                    aria-label={`Open case ${c.case_id}`}
                    style={{
                      padding: "5px 14px", borderRadius: "var(--radius-sm)",
                      border: `1px solid ${stageColor}50`, background: `${stageColor}10`,
                      color: stageColor, fontSize: 12, fontWeight: 700, cursor: "pointer",
                      whiteSpace: "nowrap",
                    }}
                  >
                    Open →
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ─── Pagination ────────────────────────────────────────────────────────────────

const DEFAULT_PAGE_SIZE = 20;

function QueuePaginationBar({
  page,
  totalPages,
  totalItems,
  pageSize,
  onPageChange,
}: {
  page: number;
  totalPages: number;
  totalItems: number;
  pageSize: number;
  onPageChange: (nextPage: number) => void;
}): React.ReactElement {
  const start = totalItems === 0 ? 0 : (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, totalItems);

  return (
    <nav
      aria-label="Case queue pagination"
      style={{
        marginTop: 16,
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 12,
        padding: "12px 16px",
        background: "var(--panel)",
        border: "1px solid var(--border)",
        borderRadius: "var(--radius)",
        boxShadow: "var(--shadow)",
      }}
    >
      <span style={{ fontSize: 13, color: "var(--muted)" }}>
        Showing{" "}
        <strong style={{ color: "var(--ink)", fontWeight: 700 }}>{start}</strong>
        {"–"}
        <strong style={{ color: "var(--ink)", fontWeight: 700 }}>{end}</strong>
        {" of "}
        <strong style={{ color: "var(--ink)", fontWeight: 700 }}>{totalItems}</strong>
        {totalPages > 1 ? (
          <span style={{ marginLeft: 8 }}>
            · Page {page} of {totalPages}
          </span>
        ) : null}
      </span>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <button
          type="button"
          onClick={() => onPageChange(page - 1)}
          disabled={page <= 1}
          aria-label="Previous page"
          style={{
            padding: "8px 14px",
            borderRadius: "var(--radius-sm)",
            border: "1px solid var(--border)",
            background: page <= 1 ? "var(--panel-soft)" : "var(--panel)",
            color: page <= 1 ? "var(--muted)" : "var(--brand)",
            fontSize: 13,
            fontWeight: 700,
            cursor: page <= 1 ? "not-allowed" : "pointer",
          }}
        >
          Previous
        </button>
        <button
          type="button"
          onClick={() => onPageChange(page + 1)}
          disabled={page >= totalPages}
          aria-label="Next page"
          style={{
            padding: "8px 14px",
            borderRadius: "var(--radius-sm)",
            border: "1px solid var(--border)",
            background: page >= totalPages ? "var(--panel-soft)" : "var(--panel)",
            color: page >= totalPages ? "var(--muted)" : "var(--brand)",
            fontSize: 13,
            fontWeight: 700,
            cursor: page >= totalPages ? "not-allowed" : "pointer",
          }}
        >
          Next
        </button>
      </div>
    </nav>
  );
}

// ─── View toggle button ───────────────────────────────────────────────────────

function ViewToggle({ view, onChange }: { view: "grid" | "table"; onChange: (v: "grid" | "table") => void }): React.ReactElement {
  const btn = (id: "grid" | "table", icon: string, label: string) => (
    <button
      onClick={() => onChange(id)}
      title={label}
      aria-label={label}
      aria-pressed={view === id}
      style={{
        width: 36, height: 36, display: "flex", alignItems: "center", justifyContent: "center",
        border: "1px solid var(--border)", cursor: "pointer", fontSize: 15,
        background: view === id ? "var(--brand)" : "var(--panel)",
        color: view === id ? "#fff" : "var(--muted)",
        borderRadius: id === "grid" ? "var(--radius-sm) 0 0 var(--radius-sm)" : "0 var(--radius-sm) var(--radius-sm) 0",
        transition: "background 0.15s, color 0.15s",
      }}
    >
      {icon}
    </button>
  );

  return (
    <div style={{ display: "flex" }}>
      {btn("grid", "⊞", "Grid view")}
      {btn("table", "☰", "Table view")}
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

interface CaseQueuePageProps {
  onSelectCase: (caseId: string) => void;
  onInitiateCase: () => void;
}

const STORAGE_KEY = "aeos_institution_queue_view";

export function CaseQueuePage({ onSelectCase, onInitiateCase }: CaseQueuePageProps): React.ReactElement {
  const [activeFilter, setActiveFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [viewMode, setViewMode] = useState<"grid" | "table">(() => {
    try { return (localStorage.getItem(STORAGE_KEY) as "grid" | "table" | null) ?? "grid"; }
    catch { return "grid"; }
  });
  const { cases, loading, error, refetch } = useCases();

  function handleViewChange(v: "grid" | "table"): void {
    setViewMode(v);
    try { localStorage.setItem(STORAGE_KEY, v); } catch { /* ignore */ }
  }

  function getCaseStage(c: Case): CaseStage {
    const flags = c.flags as unknown as Record<string, Record<string, boolean | null>>;
    return deriveCaseStage({
      institution: flags["institution"],
      marketplace: flags["marketplace"],
      hub_ops: flags["hub_ops"],
      is_closed: c.is_closed,
    }) as CaseStage;
  }

  const stageFilteredCases = useMemo(
    () => cases.filter((c) => activeFilter === "all" || getCaseStage(c) === activeFilter),
    [cases, activeFilter],
  );

  const filteredCases = useMemo(
    () => stageFilteredCases.filter((c) => caseMatchesSearchQuery(c, searchQuery)),
    [stageFilteredCases, searchQuery],
  );

  const totalFiltered = filteredCases.length;
  const totalPages = Math.max(1, Math.ceil(totalFiltered / DEFAULT_PAGE_SIZE));
  const page = Math.min(Math.max(1, currentPage), totalPages);

  useEffect(() => {
    setCurrentPage(1);
  }, [activeFilter, searchQuery]);

  useEffect(() => {
    setCurrentPage((prev) => Math.min(prev, totalPages));
  }, [totalPages]);

  const paginatedCases = useMemo(() => {
    const start = (page - 1) * DEFAULT_PAGE_SIZE;
    return filteredCases.slice(start, start + DEFAULT_PAGE_SIZE);
  }, [filteredCases, page]);

  const hasActiveSearch = searchQuery.trim().length > 0;

  const stageCounts = cases.reduce<Record<string, number>>((acc, c) => {
    const stage = getCaseStage(c);
    acc[stage] = (acc[stage] ?? 0) + 1;
    return acc;
  }, {});

  const pillItems = STAGE_FILTERS.map((f) => ({
    id: f.id,
    label: f.label,
    count: f.id === "all" ? cases.length : stageCounts[f.id],
  }));

  return (
    <>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 900, color: "var(--ink)" }}>Case Queue</h1>
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <ViewToggle view={viewMode} onChange={handleViewChange} />
          <button
            onClick={onInitiateCase}
            aria-label="Initiate new case"
            style={{
              height: 36, padding: "0 20px", borderRadius: "var(--radius-sm)",
              border: "none", background: "var(--brand)", color: "#fff",
              fontSize: 14, fontWeight: 700, cursor: "pointer",
              transition: "transform 0.16s ease",
            }}
            onMouseEnter={(e) => ((e.currentTarget as HTMLButtonElement).style.transform = "translateY(-1px)")}
            onMouseLeave={(e) => ((e.currentTarget as HTMLButtonElement).style.transform = "")}
          >
            + Initiate Case
          </button>
        </div>
      </div>

      {/* Stage filter pills */}
      <PillMenu items={pillItems} activeId={activeFilter} onSelect={setActiveFilter} />

      {/* Search — case ID, borrower, loan ref, asset */}
      <div
        style={{
          marginTop: 18,
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: 12,
        }}
      >
        <label
          htmlFor="case-queue-search"
          style={{
            fontSize: 11,
            fontWeight: 800,
            letterSpacing: "0.05em",
            textTransform: "uppercase",
            color: "var(--muted)",
            flex: "0 0 auto",
          }}
        >
          Search
        </label>
        <div style={{ position: "relative", flex: "1 1 240px", maxWidth: 420 }}>
          <input
            id="case-queue-search"
            type="search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Case ID, borrower, loan ref, asset…"
            aria-label="Search cases by case ID, borrower name, loan reference, or asset name"
            autoComplete="off"
            spellCheck={false}
            style={{
              width: "100%",
              boxSizing: "border-box",
              padding: "10px 38px 10px 14px",
              fontSize: 14,
              fontWeight: 500,
              color: "var(--ink)",
              background: "var(--panel)",
              border: "1px solid var(--border)",
              borderRadius: "var(--radius-sm)",
              outline: "none",
              boxShadow: "none",
            }}
            onFocus={(e) => {
              (e.target as HTMLInputElement).style.boxShadow = "0 0 0 2px var(--brand-soft), 0 0 0 4px rgba(29, 78, 216, 0.25)";
            }}
            onBlur={(e) => {
              (e.target as HTMLInputElement).style.boxShadow = "none";
            }}
          />
          {hasActiveSearch && (
            <button
              type="button"
              onClick={() => setSearchQuery("")}
              aria-label="Clear search"
              style={{
                position: "absolute",
                right: 8,
                top: "50%",
                transform: "translateY(-50%)",
                width: 28,
                height: 28,
                padding: 0,
                border: "none",
                borderRadius: 8,
                background: "var(--panel-soft)",
                color: "var(--muted)",
                cursor: "pointer",
                fontSize: 16,
                lineHeight: 1,
              }}
            >
              ×
            </button>
          )}
        </div>
        <span style={{ fontSize: 12, color: "var(--muted)", flex: "1 1 200px" }}>
          Matches case ID, borrower, loan reference (including FAST), and catalog / listing titles.
        </span>
      </div>

      {/* Error */}
      {error && (
        <div role="alert" style={{ background: "var(--danger-bg)", color: "var(--danger)", borderRadius: "var(--radius-sm)", padding: "12px 16px", margin: "16px 0", fontSize: 14 }}>
          Failed to load cases: {error}{" "}
          <button onClick={refetch} style={{ background: "none", border: "none", color: "var(--brand)", cursor: "pointer", fontWeight: 700 }}>Retry</button>
        </div>
      )}

      {/* Content */}
      <div style={{ marginTop: 20 }}>
        {loading ? (
          /* Skeleton always shows as grid regardless of toggle */
          <div className="card-grid">
            {[1, 2, 3, 4].map((n) => <SkeletonCard key={n} />)}
          </div>
        ) : filteredCases.length === 0 ? (
          <div style={{ textAlign: "center", padding: "60px 20px", color: "var(--muted)" }}>
            <p style={{ fontSize: 40, margin: "0 0 12px" }}>📂</p>
            <p style={{ fontSize: 16, fontWeight: 600, margin: "0 0 6px" }}>No cases found</p>
            <p style={{ fontSize: 14, margin: 0 }}>
              {cases.length === 0 ? (
                "No cases have been initiated yet."
              ) : stageFilteredCases.length === 0 ? (
                activeFilter === "all"
                  ? "No cases match the current view."
                  : `No cases in the "${activeFilter.replace(/_/g, " ")}" stage.`
              ) : (
                <>No cases match &ldquo;{searchQuery.trim()}&rdquo;. Try different keywords or clear the search.</>
              )}
            </p>
          </div>
        ) : viewMode === "grid" ? (
          <>
            <div className="card-grid">
              {paginatedCases.map((c) => (
                <RichCaseCard
                  key={c.case_id}
                  c={c}
                  stage={getCaseStage(c)}
                  onClick={() => onSelectCase(c.case_id)}
                />
              ))}
            </div>
            <QueuePaginationBar
              page={page}
              totalPages={totalPages}
              totalItems={totalFiltered}
              pageSize={DEFAULT_PAGE_SIZE}
              onPageChange={setCurrentPage}
            />
          </>
        ) : (
          <>
            <CaseTable
              cases={paginatedCases}
              getCaseStage={getCaseStage}
              onSelectCase={onSelectCase}
            />
            <QueuePaginationBar
              page={page}
              totalPages={totalPages}
              totalItems={totalFiltered}
              pageSize={DEFAULT_PAGE_SIZE}
              onPageChange={setCurrentPage}
            />
          </>
        )}
      </div>
    </>
  );
}
